import React, { ReactNode, useState, useEffect, useRef } from "react";
import clsx from "clsx";
import TooltipMarkdown from "@/components/ui/TooltipMarkdown";
import { FloatingArrow, Placement, arrow, autoUpdate, flip, offset, shift, useFloating, useHover, useInteractions } from "@floating-ui/react";
import { createPortal } from "react-dom";

/** Fade duration (ms) — must match the `tooltip-fade` animation / transition. */
const TOOLTIP_FADE_MS = 150;

export type ToolTipProps = {
  children?: ReactNode;
  text: string | ReactNode;
  className?: string;
  textClassName?: string;
  addTextClassName?: string;
  placement?: Placement
};

/**
 * @deprecated Use the `useToolTip` hook instead to avoid the `inline-block` wrapper div.
 * Import from `@/hooks/useToolTip`.
 */
export const ToolTip: React.FC<ToolTipProps> = ({ children, text, className, textClassName, addTextClassName, placement }) => {
  const [show, setShow] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null);
  // const tooltipRef = useRef<HTMLDivElement>(null);

  // Fade-out support — see `useToolTip`. A keyframe fades the tooltip in on
  // mount; this keeps the node mounted for one fade duration after `show` goes
  // false so the opacity transition can fade it out before unmounting.
  const [exiting, setExiting] = useState(false);
  const wasShownRef = useRef(false);
  useEffect(() => {
    if (show) {
      setExiting(false);
      wasShownRef.current = true;
      return;
    }
    if (!wasShownRef.current) return;
    setExiting(true);
    wasShownRef.current = false;
    const timer = setTimeout(() => setExiting(false), TOOLTIP_FADE_MS);
    return () => clearTimeout(timer);
  }, [show]);

  const { refs, floatingStyles, context } = useFloating({
    open: show,
    onOpenChange: setShow,
    strategy: "fixed",
    placement: placement || "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      arrow({
        element: arrowRef
      }),
      offset({
        mainAxis: 10
      }),
      flip({
        padding: 40
      }),
      shift()
    ]
  });

  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover
  ]);

  const tooltipClass = `${textClassName || "bg-black px-2 py-1 text-white rounded min-w-[min-content] whitespace-nowrap"}` + (addTextClassName || "");

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={`inline-block ${className || ""}`}
      >
        {children}
      </div>

      {typeof window !== "undefined" && createPortal(
        (show || exiting) && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={clsx(
              "z-[9999] pointer-events-none transition-opacity",
              show ? "animate-tooltip-fade" : "opacity-0",
            )}
          >
            <div className={tooltipClass}>
              <FloatingArrow
                ref={arrowRef}
                context={context}
              />
              { typeof text === "string" ?  <TooltipMarkdown
                  className="max-w-[50dvw]"
                  children={text}
                /> : text }
            </div>
          </div>
        )
        , document.body
      )}
    </>
  );
};
